import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import anish3d from '../../assets/anish-3d.webp';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import './home.scss';

gsap.registerPlugin(ScrollTrigger);

const Home = () => {
    const heroRef = useRef(null);
    const imageRef = useRef(null);
    const titleRef = useRef(null);
    const subtitleRef = useRef(null);
    const ctaRef = useRef(null);

    useEffect(() => {
        const ctx = gsap.context(() => {
            const tl = gsap.timeline();

            // Set initial states
            gsap.set(ctaRef.current.children, { scale: 1, opacity: 1 });

            // Title entrance
            tl.from(titleRef.current.children, {
                y: 100,
                opacity: 0,
                rotationX: -90,
                transformOrigin: 'top center',
                duration: 0.8,
                stagger: 0.15,
                ease: 'power4.out'
            });

            tl.from(subtitleRef.current, {
                y: 40,
                opacity: 0,
                duration: 0.6,
                ease: 'power3.out'
            }, '-=0.5');

            tl.fromTo(ctaRef.current.children, 
                {
                    scale: 0,
                    opacity: 0
                },
                {
                    scale: 1,
                    opacity: 1,
                    duration: 0.5,
                    stagger: 0.1,
                    ease: 'back.out(1.7)'
                }, 
                '-=0.3'
            );

            // Image entrance
            gsap.from(imageRef.current, {
                scale: 0.5,
                opacity: 0,
                duration: 1.5,
                ease: 'power4.out',
                delay: 0.3
            });

            // Parallax scroll
            gsap.to(imageRef.current, {
                scrollTrigger: {
                    trigger: heroRef.current,
                    start: 'top top',
                    end: 'bottom top',
                    scrub: 1.5
                },
                y: 100,
                ease: 'none'
            });

        }, heroRef);

        return () => ctx.revert();
    }, []);

    const handleMouseMove = (e) => {
        const { clientX, clientY } = e;
        const { innerWidth, innerHeight } = window;
        
        // Calculate movement based on mouse position (range: -30 to 30)
        const xMove = ((clientX / innerWidth) - 0.5) * 60;
        const yMove = ((clientY / innerHeight) - 0.5) * 60;

        // Smooth GSAP animation following mouse
        gsap.to(imageRef.current, {
            x: xMove,
            y: yMove,
            duration: 0.8,
            ease: 'power2.out'
        });
    };

    const handleMouseLeave = () => {
        // Return to center position when mouse leaves
        gsap.to(imageRef.current, {
            x: 0,
            y: 0,
            duration: 1,
            ease: 'power2.out'
        });
    };

    return (
        <section className="home" ref={heroRef} onMouseMove={handleMouseMove} onMouseLeave={handleMouseLeave}>
            <div className="home-container">
                <div className="home-content">
                    <div className="text-content">
                        <div className="label-badge">
                            <div className="status-dot"></div>
                            Available for Hire
                        </div>
                        
                        <h1 className="hero-title" ref={titleRef}>
                            <span className="title-line">Senior Frontend</span>
                            <span className="title-line gradient-text">Developer</span>
                        </h1>
                        
                        <p className="hero-subtitle" ref={subtitleRef}>
                            Senior frontend engineer and <strong>open-source builder</strong> with <strong>8+ years</strong> shipping web and mobile products.
                            Creator of <strong>AniUI</strong> — a React Native design system with <strong>149 components</strong> — plus AniUI Pro, Academy, and <strong>anishl.dev</strong>.
                        </p>
                        
                        <div className="cta-group" ref={ctaRef}>
                            <a 
                                href="#contact" 
                                className="btn-primary"
                                onClick={(e) => {
                                    e.preventDefault();
                                    document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                }}
                            >
                                Get in Touch
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                                    <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                            </a>
                            <Link to="/resume" className="btn-secondary">
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                    <polyline points="14 2 14 8 20 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                    <line x1="16" y1="13" x2="8" y2="13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                    <line x1="16" y1="17" x2="8" y2="17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                    <polyline points="10 9 9 9 8 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                                View Resume
                            </Link>
                        </div>

                        <div className="tech-stack">
                            <span className="stack-label">Tech Stack</span>
                            <div className="stack-icons">
                                <div className="stack-item">React</div>
                                <div className="stack-item">React Native</div>
                                <div className="stack-item">TypeScript</div>
                                <div className="stack-item">AniUI</div>
                            </div>
                        </div>
                    </div>
                    
                    <div className="image-content">
                        <div className="image-wrapper" ref={imageRef}>
                            <img src={anish3d} alt="Anish Lawrence - Senior Frontend Developer" className="hero-image" />
                        </div>
                    </div>
                </div>
            </div>

            <div className="scroll-hint">
                <span>Scroll</span>
                <div className="scroll-line"></div>
            </div>
        </section>
    );
};

export default Home;
